import { IFlightTemplate, INewFlightTemplate } from "@vmosolutions/oc-interfaces";
import { API, Auth } from "aws-amplify";
import _, { cloneDeep } from "lodash";
import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import AmplifyAPIWrapper, { INotifications } from "../../API/AmplifyAPIWrapper";
import { IRootState } from "../reducers/CombinedReducers";
import { openSnackbar } from "./App.Actions";

/**
 * String variables representing the TYPES of actions redux can perform
 */
export const GOT_FLIGHT_BUILDER_TEMPLATES = "GOT_FLIGHT_BUILDER_TEMPLATES";
export const SET_ACTIVE_NODE = "SET_ACTIVE_NODE";
export const INSERT_NEW_NODE = "INSERT_NEW_NODE";
export const REMOVE_NODE = "REMOVE_NODE";
export const COPY_NODE = "COPY_NODE";
export const PASTE_NODE = "PASTE_NODE";
export const SET_CONTEXT_ANCHOR = "SET_CONTEXT_ANCHOR";
export const SET_ACTIVE_FILE_TEMPLATE = "SET_ACTIVE_TEMPLATE_FILE_NODE";
export const UPDATE_FILE_TEMPLATE = "UPDATE_FILE_TEMPLATE";
export const SET_RENAME_NODE_ID = "SET_RENAME_NODE_ID";

/**
 * Action structure for setting the flightBuilder Templates data
 */
export interface IGotFlightBuilderTemplates {
  type: typeof GOT_FLIGHT_BUILDER_TEMPLATES;
  templates: IFlightTemplate[];
  activeFileTemplate: IFlightTemplate;
}

/**
 * Action structure for setting the active node
 */
export interface ISetActiveNode {
  type: typeof SET_ACTIVE_NODE;
  activeNode: IFlightTemplate;
}

/**
 * Action structure for insertting new flight template node into tree heirarchy
 */
export interface IInsertNewNode {
  type: typeof INSERT_NEW_NODE;
  templates: IFlightTemplate;
}

export interface IRemoveNode {
  type: typeof REMOVE_NODE;
  templates: IFlightTemplate;
}

export interface ICopyNode {
  type: typeof COPY_NODE;
  node: IFlightTemplate;
}

export interface IPasteNode {
  type: typeof PASTE_NODE;
  templates: IFlightTemplate;
}

export interface ISetContextAnchor {
  type: typeof SET_CONTEXT_ANCHOR;
  anchorEl: any;
}

export interface ISetActiveTemplateFileNode {
  type: typeof SET_ACTIVE_FILE_TEMPLATE;
  activeFileTemplate: IFlightTemplate;
}

export interface IUpdateFlightTemplate {
  type: typeof UPDATE_FILE_TEMPLATE;
}

export interface ISetRenameMode {
  type: typeof SET_RENAME_NODE_ID;
  renameNodeId: string | null;
}
/**
 * Action all the available actions for FlightBuilder
 */
export type Action =
  | IUpdateFlightTemplate
  | ISetActiveTemplateFileNode
  | ISetContextAnchor
  | ICopyNode
  | IPasteNode
  | IRemoveNode
  | IInsertNewNode
  | ISetActiveNode
  | IGotFlightBuilderTemplates
  | ISetRenameMode;

/**
 * setActiveTemplateNode an action that sets the activeNodeId in redux
 * @param activeNodeId - a string of the id of the active file tree node to set in redux
 * @param path - An array of strings each representing one level down the file tree heirarchy.
 */
export const setActiveTemplateNode = (activeNode: IFlightTemplate): ISetActiveNode => {
  return { type: SET_ACTIVE_NODE, activeNode };
};

export const setRenameNodeId = (renameNodeId: string | null): ISetRenameMode => {
  return { type: SET_RENAME_NODE_ID, renameNodeId };
};

/**
 * setActiveFileTemplate an action that sets active fileNode in redux
 * This in turn will display the flight template in more detail where users can modify and/or build flights
 * based off of this template
 * @param activeFileTemplate - The file template that will be active
 */
export const setActiveFileTemplate = (activeFileTemplate: IFlightTemplate): ISetActiveTemplateFileNode => {
  return { type: SET_ACTIVE_FILE_TEMPLATE, activeFileTemplate };
};

/**
 * getFlightBuilderTemplates: function that returns an asynchronous ThunkAction called getFlightBuilderAction.
 * See below for details on getFlightBuilderAction
 */
export const getFlightBuilderTemplates = (): ThunkAction<
  Promise<void>,
  IRootState,
  object,
  IGotFlightBuilderTemplates
> => {
  /**
   * getFlightBuilderAction: asynchronous function that calls the serverless backend to retrieve flightBuilderTemplates.
   * Once retrieved dispatches the GOT_FLIGHT_BUILDER_TEMPLATES action to store in redux
   * @param dispatch - A ThunkDispatch taking the root state, and the dispatch action IGotFlightBuilderTemplates
   * call this to dispatch action after we have retrieved the flightBuilderTemplates
   * @param getState - A function that returns the root state of redux.
   */
  const getFlightBuilderAction = async (
    dispatch: ThunkDispatch<IRootState, object, IGotFlightBuilderTemplates>,
    getState: () => IRootState
  ): Promise<void> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();
    const allTemplates: IFlightTemplate[] = [];
    const activeFileTemplate = getState().flightBuilderTemplates.activeFileTemplate;
    let doMoreCalls = true;
    let nextId = null;

    while (doMoreCalls === true) {
      const results: any = await API.get("skytrains", `/flight-builder/${nextId ? `?nextId=${nextId}` : ""} `, {
        headers: {
          Accept: "*/*",
          Authorization: token,
        },
      });
      nextId = null;
      if (results) {
        allTemplates.push(...results.templates);
        if (results.pagination.nextId) {
          nextId = results.pagination.nextId;
        } else {
          doMoreCalls = false;
          nextId = null;
          dispatch({
            type: GOT_FLIGHT_BUILDER_TEMPLATES,
            templates: allTemplates,
            activeFileTemplate,
          });
        }
      }
    }
  };
  return getFlightBuilderAction;
};

/**
 * updateFlightBuilderTemplates: function that returns an asynchronous ThunkAction called updateFlightBuilderAction.
 * @param updatedTemplates - the array of new templates to update
 * @returns updateFlightBuilderAction See below for details on updateFlightBuilderAction
 */
export const updateFlightBuilderTemplates = (
  updatedTemplates: IFlightTemplate[]
): ThunkAction<void, IRootState, object, IGotFlightBuilderTemplates> => {
  /**
   * updateFlightBuilderAction: asynchronous function that updates the current list of flightTemplates
   * @param dispatch - A ThunkDispatch taking the root state, and the dispatch action IGotFlightBuilderTemplates
   * @param getState - A function that returns the root state of redux.
   */
  const updateFlightBuilderAction = (
    dispatch: ThunkDispatch<IRootState, object, IGotFlightBuilderTemplates>,
    getState: () => IRootState
  ): void => {
    let activeFileTemplate = getState().flightBuilderTemplates.activeFileTemplate;

    const templates = _.cloneDeep(getState().flightBuilderTemplates.templates);
    updatedTemplates.forEach((updatedTemplate: IFlightTemplate) => {
      const index = templates.findIndex((value: IFlightTemplate) => value._id === updatedTemplate._id);
      if (activeFileTemplate && activeFileTemplate._id === updatedTemplate._id) {
        activeFileTemplate = { ...updatedTemplate };
      }
      if (index >= 0) {
        templates[index] = { ...updatedTemplate };
      } else {
        templates.push(updatedTemplate);
      }
    });
    dispatch({
      type: GOT_FLIGHT_BUILDER_TEMPLATES,
      templates,
      activeFileTemplate,
    });
  };
  return updateFlightBuilderAction;
};

/**
 * updateFileTemplate: function that returns an asynchronous ThunkAction called updateFlightTemplateAction.
 * @param updatedTemplate - the updated flight template to save
 * @returns updateFlightTemplateAction See below for details on updateFlightBuilderAction
 */
export const updateFileTemplate = (
  updatedTemplate: IFlightTemplate
): ThunkAction<Promise<void>, IRootState, object, IGotFlightBuilderTemplates> => {
  /**
   * updateFlightTemplateAction: asynchronous function that updates the current list of flightTemplates
   * @param dispatch - A ThunkDispatch taking the root state, and the dispatch action IGotFlightBuilderTemplates
   * @param getState - A function that returns the root state of redux.
   */
  const updateFlightTemplateAction = async (
    dispatch: ThunkDispatch<IRootState, object, IGotFlightBuilderTemplates | AnyAction>,
    getState: () => IRootState
  ): Promise<any> => {
    const userSession = await Auth.currentSession();
    const token = userSession.getIdToken().getJwtToken();
    const { _id } = updatedTemplate;
    if (!_id) {
      return;
    }

    dispatch(
      openSnackbar({
        open: true,
        closeDelay: null,
        variant: "info",
        message: "Saving Template Changes...",
      })
    );

    const response = await API.put("skytrains", `/flight-builder/${_id}`, {
      headers: {
        Accept: "*/*",
        Authorization: token,
      },
      body: updatedTemplate,
    });
    //const promise = Promise;
    if (!!response && typeof response === "object" && !!response.hasOwnProperty("newFlightTemplate")) {
      // Update this active template in tree node.
      dispatch(updateFlightBuilderTemplates(response.newFlightTemplate));

      // SUCCESS
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "success",
          message: "Template Changes Saved Successfully.",
        })
      );
      //promise.resolve(response)
    } else {
      // FAILURE
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: "Something Went Wrong!",
        })
      );
      //promise.reject(response)
    }
    //return promise;
  };
  return updateFlightTemplateAction;
};

/**
 * deleteFlightBuilderTemplates: function that returns an asynchronous ThunkAction called deleteFlightBuilderAction.
 * @param templateIds - the array of template ids to remove
 * @returns deleteFlightBuilderAction See below for details on deleteFlightBuilderAction
 */
export const deleteFlightBuilderTemplates = (
  deletedTemplateIds: string[]
): ThunkAction<void, IRootState, object, IGotFlightBuilderTemplates> => {
  /**
   * deleteFlightBuilderAction: asynchronous function that deletes the current list of flightTemplate ids
   * @param dispatch - A ThunkDispatch taking the root state, and the dispatch action IGotFlightBuilderTemplates
   * @param getState - A function that returns the root state of redux.
   */
  const deleteFlightBuilderAction = (
    dispatch: ThunkDispatch<IRootState, object, IGotFlightBuilderTemplates>,
    getState: () => IRootState
  ): void => {
    const activeFileTemplate = getState().flightBuilderTemplates.activeFileTemplate;
    const templates = _.cloneDeep(getState().flightBuilderTemplates.templates);
    const newTemplates = templates.filter(
      (value: IFlightTemplate) => deletedTemplateIds.findIndex((id: string) => id === value._id) < 0
    );
    dispatch({
      type: GOT_FLIGHT_BUILDER_TEMPLATES,
      templates: newTemplates,
      activeFileTemplate,
    });
  };
  return deleteFlightBuilderAction;
};

/**
 * insertNewFolder - appends a new template folder in the tree, dispatches action to redux
 * If a directory is selected, appends the new folder as a child,
 * If a file is selected appends the folder as a sibling to the file
 * If nothing is selected appends to root
 */
export const insertNewFolder = (): ThunkAction<Promise<void>, IRootState, object, IInsertNewNode> => {
  /**
   * insertNewFolderAction - Thunk action to carry out insertNewFolder
   * @param dispatch - A ThunkDispatch taking the root state, and the dispatch action IGotFlightBuilderTemplates
   * call this to dispatch action after we have inserted the folder node
   * @param getState - A function that returns the root state of redux.
   */
  const insertNewFolderAction = async (
    dispatch: ThunkDispatch<IRootState, object, IInsertNewNode>,
    getState: () => IRootState
  ): Promise<void> => {
    const activeNode = getState().flightBuilderTemplates.activeNode;
    const path = activeNode ? (activeNode.isLeafNode ? activeNode.path : [...activeNode.path, activeNode._id]) : [];
    const newDirectory: INewFlightTemplate = {
      fileName: "New Folder",
      path: [],
      isLeafNode: false,
    };

    await insertNewFlightTemplateNodes([newDirectory], path, dispatch, "new_folder");
  };
  return insertNewFolderAction;
};

/**
 * insertNewFile - appends a new template file in the tree, dispatches action to redux
 * If a file is selected, appends the new file as a child,
 * If a file is selected appends the file as a sibling to the file
 * If nothing is selected appends to root
 */
export const insertNewFile = (): ThunkAction<Promise<void>, IRootState, object, IInsertNewNode> => {
  /**
   * insertNewFileAction - Thunk action to carry out insertNewFile
   * @param dispatch - A ThunkDispatch taking the root state, and the dispatch action insertNewFileAction
   * call this to dispatch action after we have inserted the file node
   * @param getState - A function that returns the root state of redux.
   */
  const insertNewFileAction = async (
    dispatch: ThunkDispatch<IRootState, object, IInsertNewNode>,
    getState: () => IRootState
  ): Promise<void> => {
    const activeNode = getState().flightBuilderTemplates.activeNode;
    const path = activeNode ? (activeNode.isLeafNode ? activeNode.path : [...activeNode.path, activeNode._id]) : [];
    const nodeToInsert: INewFlightTemplate = {
      fileName: "New File Template",
      path: [],
      isLeafNode: true,
    };
    await insertNewFlightTemplateNodes([nodeToInsert], path, dispatch);
  };
  return insertNewFileAction;
};

export interface INewTemplateReturn {
  newTemplates: IFlightTemplate[]; //ITemplateTreeNode[];
}
/**
 * insertNewFlightTemplateNode - calls backend api to insert a node template into the flightBuilder template tree.
 * @param nodeToInsert - - insert either a directory or file template node into the tree
 */
export const insertNewFlightTemplateNodes = async (
  nodesToInsert: INewFlightTemplate[],
  referencePath: string[],
  dispatch: ThunkDispatch<object, object, any>,
  mode?: "copypaste" | "new_folder"
): Promise<INewTemplateReturn> => {
  const body = { templates: [...nodesToInsert], referencePath: referencePath };
  const notifications: INotifications = {
    dispatch: dispatch,
    infoMessage:
      mode === "new_folder" ? "Creating New Folder" : mode === "copypaste" ? "Copying Item" : "Creating New Template",
    successMessage:
      mode === "new_folder"
        ? "Folder Successfully Created"
        : mode === "copypaste"
          ? "Item Successfully Copied"
          : "Template Successfully Created",
    errorMessage:
      mode === "new_folder"
        ? "Error: Unable to Create Folder"
        : mode === "copypaste"
          ? "Error: Unable to Copy Item"
          : "Error: Unable to Create Template",
  };

  // This response returns an array of Templates. These can be new templates or moved templates.
  const response = await AmplifyAPIWrapper.Post<INewTemplateReturn>("/flight-builder", body, notifications);
  if (Array.isArray(response?.newTemplates) && response?.newTemplates?.length) {
    dispatch(updateFlightBuilderTemplates(response.newTemplates));
  }

  return response;
};

// export const pasteFlightTemplateNodes = async(templates: INewFlightTemplate[], referencePath: string[])

const getChildrenNodeIds = (activeNode: IFlightTemplate, templates: IFlightTemplate[]): string[] => {
  const path = [...activeNode.path, activeNode._id];
  const pathStr = path.join(",");
  const childrenNodes = templates.filter((template: IFlightTemplate) => template.path.join(",").startsWith(pathStr));
  return childrenNodes.map((node: IFlightTemplate): string => node._id);
};

/**
 * removeTemplateNode - Removes the active node from the tree heirarchy.
 * The active node is set when user right clicks so that the active node is definitely the right one to delete
 * @returns ThunkAction function
 */
export const removeTemplateNode = (): ThunkAction<Promise<void>, IRootState, object, IRemoveNode> => {
  /**
   * removeTempaleNodeAction - Thunk action to carry out removing the active node
   * @param dispatch - A ThunkDispatch taking the root state, and the dispatch action insertNewFileAction
   * call this to dispatch action after we have inserted the file node
   * @param getState - A function that returns the root state of redux.
   */
  const removeTemplateNodeAction = async (
    dispatch: ThunkDispatch<IRootState, object, AnyAction>,
    getState: () => IRootState
  ): Promise<void> => {
    const activeNode = cloneDeep(getState().flightBuilderTemplates.activeNode);

    if (!activeNode) {
      return;
    }

    const templates = getState().flightBuilderTemplates.templates;
    const templateIds = activeNode.isLeafNode
      ? [activeNode._id]
      : [activeNode._id, ...getChildrenNodeIds(activeNode, templates)];
    const body = [...templateIds];
    const isFolder = activeNode.isLeafNode ? "" : "Folder";
    const notifications: INotifications = dispatch
      ? {
          dispatch: dispatch,
          infoMessage: `Deleting Template ${isFolder}`,
          successMessage: `Template ${isFolder} Successfully Deleted`,
          errorMessage: `Error: Unable to Delete Template ${isFolder}`,
        }
      : null;

    // This response returns an array of Template ids to remove from the tree.
    const response = await AmplifyAPIWrapper.Del<any>("/flight-builder", body, notifications);
    if (Array.isArray(response?.templateIds) && response?.templateIds?.length) {
      dispatch(deleteFlightBuilderTemplates(response.templateIds));
    }
  };

  return removeTemplateNodeAction;
};

/**
 * copyTemplateNode - copies the active node from the tree heirarchy.
 * The active node is set when user right clicks so that the active node is definitely the right one to copy
 * @returns ThunkAction function
 */
export const copyTemplateNode = (): ThunkAction<void, IRootState, object, ICopyNode> => {
  /**
   * copyTemplateNodeAction - Thunk action to carry out removing the active node
   * @param dispatch - A ThunkDispatch taking the root state, and the dispatch action insertNewFileAction
   * call this to dispatch action after we have inserted the file node
   * @param getState - A function that returns the root state of redux.
   */
  const copyTemplateNodeAction = (
    dispatch: ThunkDispatch<IRootState, object, ICopyNode>,
    getState: () => IRootState
  ): void => {
    const activeNode = cloneDeep(getState().flightBuilderTemplates.activeNode);
    dispatch({ type: COPY_NODE, node: activeNode });
  };

  return copyTemplateNodeAction;
};

const getNodePath = (node: IFlightTemplate): any[] => {
  return node.isLeafNode ? node.path : [...node.path, node._id];
};
/**
 * pasteTemplateNode - pastes the copied node from the tree heirarchy.
 * @returns ThunkAction function
 */
export const pasteTemplateNode = (): ThunkAction<Promise<void>, IRootState, object, ICopyNode> => {
  /**
   * pasteTemplateNodeAction - Thunk action to carry out pasting the copied node
   * @param dispatch - A ThunkDispatch taking the root state, and the dispatch action
   * @param getState - A function that returns the root state of redux.
   */
  const pasteTemplateNodeAction = async (
    dispatch: ThunkDispatch<IRootState, object, ICopyNode>,
    getState: () => IRootState
  ): Promise<void> => {
    const copiedNode = cloneDeep(getState().flightBuilderTemplates.copiedNode);
    if (!copiedNode) {
      return;
    }
    const activeNode = cloneDeep(getState().flightBuilderTemplates.activeNode);
    const templates = getState().flightBuilderTemplates.templates;
    const newBasePath = activeNode ? getNodePath(activeNode) : [];

    const newCopiedNode: INewFlightTemplate = {
      ...copiedNode,
      path: [],
      fileName: copiedNode.fileName + " (copy)",
      _id: undefined,
    };
    const newTemplates: INewFlightTemplate[] = copiedNode.isLeafNode
      ? [newCopiedNode]
      : [newCopiedNode, ...getChildNodesFromDirectory(copiedNode, templates)];
    await insertNewFlightTemplateNodes(newTemplates, newBasePath, dispatch, "copypaste");
  };

  return pasteTemplateNodeAction;
};

const getChildNodesFromDirectory = (node: IFlightTemplate, templates: IFlightTemplate[]): any[] => {
  if (node.isLeafNode) {
    return [];
  }
  const path = [...node.path, node._id];
  const pathStr = path.join(",");
  const oldChildrenNodes = templates.filter((template: IFlightTemplate) => template.path.join(",").startsWith(pathStr));
  const childNodes = oldChildrenNodes.map((childNode: IFlightTemplate): INewFlightTemplate => {
    const index = childNode.path.findIndex((pathNode: string) => pathNode === node._id);
    return {
      ...childNode,
      fileName: childNode.fileName + " (copy)",
      path: index >= 0 ? childNode.path.slice(index) : [],
      _id: undefined,
    };
  });
  return [...childNodes];
};

/**
 * setContextAnchor sets the div element in redux so that the context menu knows where to position itself
 * @param anchorEl - div element that the context menu can position itself to.
 */
export const setContextAnchor = (anchorEl: HTMLDivElement): any => {
  return { type: SET_CONTEXT_ANCHOR, anchorEl };
};
